import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { resolveExperienceHubPath } from '../utils/salonHomeNav';
import { isSalonDeskMode } from '../utils/salonKioskMode';
import {
  SALON_CHECKIN_UPDATED_EVENT,
  getActiveCheckinSession,
} from '../utils/salonFaceSession';
import { finalizeOpenVisitOnLogout } from '../utils/visitHandoff';
import { useFranchiseFlowAuthActions } from '../../shared/FranchiseFlowAuthContext';
import { getEnterpriseSalonModalRoot } from '../../../shared/modalRoots';
import '../styles/salon-page-nav.css';
import './SalonHeaderActions.css';

function HomeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const SalonHeaderActions = ({
  homeButtonClassName,
  logoutButtonClassName,
  onHome,
  onLogout,
  logoutBusy = false,
  showHome = true,
}) => {
  const navigate = useNavigate();
  const franchiseAuth = useFranchiseFlowAuthActions();
  const resolvedOnLogout = onLogout ?? franchiseAuth?.onLogout;
  const resolvedLogoutBusy = logoutBusy || franchiseAuth?.logoutBusy;
  const [activeCheckin, setActiveCheckin] = useState(() => getActiveCheckinSession());
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutSubmitting, setLogoutSubmitting] = useState(false);
  const [logoutError, setLogoutError] = useState('');

  useEffect(() => {
    const sync = () => setActiveCheckin(getActiveCheckinSession());
    sync();
    window.addEventListener(SALON_CHECKIN_UPDATED_EVENT, sync);
    window.addEventListener('focus', sync);
    return () => {
      window.removeEventListener(SALON_CHECKIN_UPDATED_EVENT, sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const hasOpenVisit =
    activeCheckin?.id &&
    activeCheckin.status !== 'completed' &&
    activeCheckin.status !== 'cancelled';

  const handleHome = useCallback(() => {
    if (typeof onHome === 'function') {
      onHome();
      return;
    }
    navigate(resolveExperienceHubPath());
  }, [navigate, onHome]);

  const handleConfirmLogout = async () => {
    if (typeof resolvedOnLogout !== 'function' || resolvedLogoutBusy || logoutSubmitting) return;
    setLogoutSubmitting(true);
    setLogoutError('');
    try {
      if (!isSalonDeskMode()) {
        await finalizeOpenVisitOnLogout({ complete: false });
      }
      setActiveCheckin(null);
      setLogoutConfirmOpen(false);
      await resolvedOnLogout();
    } catch (err) {
      setLogoutError(err?.message || 'Could not sign out');
    } finally {
      setLogoutSubmitting(false);
    }
  };

  return (
    <>
      <div className="salon-header-actions" role="group" aria-label="Header actions">
        {showHome ? (
          <button
            type="button"
            className={homeButtonClassName}
            onClick={handleHome}
            aria-label="Go to home"
          >
            <HomeIcon />
          </button>
        ) : null}
        {typeof resolvedOnLogout === 'function' ? (
          <button
            type="button"
            className={logoutButtonClassName || homeButtonClassName}
            onClick={() => {
              setLogoutError('');
              setLogoutConfirmOpen(true);
            }}
            disabled={resolvedLogoutBusy || logoutSubmitting}
            aria-label={resolvedLogoutBusy || logoutSubmitting ? 'Signing out' : 'Sign out'}
            title="Sign out"
          >
            <LogoutIcon />
          </button>
        ) : null}
      </div>

      {logoutConfirmOpen
        ? createPortal(
            <div
              className="salon-logout-modal-overlay"
              role="presentation"
              onClick={() => {
                if (!logoutSubmitting && !resolvedLogoutBusy) setLogoutConfirmOpen(false);
              }}
            >
              <div
                className="salon-logout-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="salon-logout-confirm-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="salon-logout-modal-icon" aria-hidden="true">
                  <LogoutIcon />
                </div>
                <h2 id="salon-logout-confirm-title" className="salon-logout-modal-title">
                  Sign out of this kiosk?
                </h2>
                <p className="salon-logout-modal-description">
                  {hasOpenVisit
                    ? 'A guest visit is still open. Signing out clears this device only — finish the visit from Service session or the partner check-in list.'
                    : 'This ends the franchise device session. You will need to sign in again to continue.'}
                </p>
                {logoutError ? (
                  <p className="salon-logout-modal-error">{logoutError}</p>
                ) : null}
                <div className="salon-logout-modal-actions">
                  <button
                    type="button"
                    className="salon-logout-modal-button salon-logout-modal-button-secondary"
                    disabled={logoutSubmitting || resolvedLogoutBusy}
                    onClick={() => setLogoutConfirmOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="salon-logout-modal-button salon-logout-modal-button-primary"
                    disabled={logoutSubmitting || resolvedLogoutBusy}
                    onClick={handleConfirmLogout}
                  >
                    {logoutSubmitting || resolvedLogoutBusy ? 'Signing out…' : 'Confirm sign out'}
                  </button>
                </div>
              </div>
            </div>,
            getEnterpriseSalonModalRoot(),
          )
        : null}
    </>
  );
};

export default SalonHeaderActions;
